import isEmpty from 'lodash/isEmpty';
import noop from 'lodash/noop';
import React, { createContext, ReactNode, useContext, useState } from 'react';

import { ThemeQuery, ThemesQuery, useMembershipQuery, useWeddingQuery } from '../../generated/graphql';
import useThemes from './useThemes';
import { OnboardingContext } from '../OnboardingContext';

interface TrackingProps<T> {
  [K: string]: T;
}

export type Track = (eventOrStep: string, trackingData?: TrackingProps<any>, options?: Record<string, any>) => void;
export type TrackBuilderInteraction = (
  eventOrStep: string,
  trackingData?: TrackingProps<any>,
  options?: Record<string, any>
) => Promise<void>;
type Identify = () => void;
type Theme = ThemeQuery['theme'];
type ThemeFamily = ThemesQuery['familyThemes'][0];
type ImpressionState = { [themeId: number]: boolean };

interface ContextProps {
  track: Track;
  trackBuilderInteraction: TrackBuilderInteraction;
  identify: Identify;
  addCarouselImpression: (id: number) => void;
  checkCarouselImpression: (id: number) => boolean;
  addGridImpression: (id: number) => void;
  checkGridImpression: (id: number) => boolean;
  isLoggedIn: boolean | null;
}

interface ProviderProps {
  children: ReactNode | ReactNode[];
}

export const getThemeBaseProps = (
  theme: Theme,
  index: number,
  colorwayIndex: number,
  familyTheme: ThemeFamily | null
) => {
  const hasColorways = (familyTheme?.themes || []).length > 1;
  return {
    colorwayPosition: hasColorways ? colorwayIndex + 1 : null,
    colorwayTheme: hasColorways,
    designName: theme?.name,
    familyName: theme?.family || theme?.name,
    partnerName: theme?.themeDesigner?.companyName || theme?.themeDesigner?.name || null,
    themeId: theme?.id,
    themePosition: index + 1,
  };
};

export const useTrackImpressions = (): [(id: number) => void, (id: number) => boolean] => {
  const impressionState: ImpressionState = {};
  const addTrack = (id: number) => (impressionState[id] = true);
  const checkTracked = (id: number) => !!impressionState[id];
  return [addTrack, checkTracked];
};

const TrackingContext = createContext<ContextProps>({
  track: noop,
  trackBuilderInteraction: () => Promise.resolve(),
  identify: noop,
  addCarouselImpression: noop,
  checkCarouselImpression: () => false,
  addGridImpression: noop,
  checkGridImpression: () => false,
  isLoggedIn: null,
});

export const TrackingProvider: React.FC<ProviderProps> = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean | null>(null);
  const { themes, sortOrder } = useThemes();
  const { data: membershipData, loading: membershipLoading } = useMembershipQuery();
  const { data: weddingData } = useWeddingQuery();
  const { firstName, lastName, fianceFirstName, fianceLastName } = useContext(OnboardingContext);

  // Set isLoggedIn state only on initial membership load; for logged out users, membership data will change after sign up.
  if (!membershipLoading && isLoggedIn === null) {
    setIsLoggedIn(!isEmpty(membershipData?.member));
  }

  const pinned = sortOrder === 'pinned';

  const baseProps = {
    createGridOrder: pinned ? 'positioned' : 'random',
    createType: isLoggedIn ? 'logged in' : 'logged out',
    platform: /(iPhone|iPod|BlackBerry|Android)/.test(navigator.userAgent) ? 'mobile' : 'web',
    product: 'Guest Services',
    themeCount: themes.length,
    wwsTemplateVersion: 'v2',
    gitHash: process?.env?.REACT_APP_GIT_REVISION || '',
  };

  const tkwwEntities = {
    tkwwEntities: {
      member: {
        memberId: membershipData?.member?.id,
        email: membershipData?.member?.email,
        firstName: firstName || membershipData?.member?.firstName,
        lastName: lastName || membershipData?.member?.lastName,
      },
    },
  };

  const track = (event: string, trackingData?: TrackingProps<any>, options?: Record<string, any>) => {
    (window as any).analytics.track(event, { ...baseProps, ...tkwwEntities, ...(trackingData || {}) }, options);
  };

  const trackBuilderInteraction = (step: string, trackingData?: TrackingProps<any>) => {
    return new Promise<void>(resolve => {
      try {
        (window as any).analytics.track(
          'WWS Builder Interaction',
          {
            ...baseProps,
            stepTitle: step,
            hasWWSWeddingDate: Boolean(weddingData?.wedding?.weddingDate),
            hasWWSWeddingLocation: Boolean(weddingData?.wedding?.market?.city),
            ...(trackingData || {}),
          },
          () => {
            resolve();
          }
        );
      } catch {
        resolve();
      }
    });
  };

  const identify = () => {
    const userId = membershipData?.member?.legacyUserId;
    (window as any).analytics.identify(userId, {
      accountCreateMarketCode: weddingData?.wedding?.market?.code,
      fianceFirstName: fianceFirstName || membershipData?.member?.fianceFirstName,
      fianceLastName: fianceLastName || membershipData?.member?.fianceLastName,
      firstName: firstName || membershipData?.member?.firstName,
      lastName: lastName || membershipData?.member?.lastName,
      memberId: membershipData?.member?.id,
      userId,
      username: membershipData?.member?.username,
      weddingBudget: weddingData?.wedding?.budgetRange,
      weddingDate: weddingData?.wedding?.weddingDate?.replace(/^(\d{2})\/(\d{2})\/(\d{4})$/, '$3-$1-$2'),
      weddingLocation: weddingData?.wedding?.market
        ? `${weddingData?.wedding?.market?.city}, ${weddingData?.wedding?.market?.state}`
        : undefined,
      weddingMarketCode: weddingData?.wedding?.market?.code,
      weddingSize: weddingData?.wedding?.guestRange,
    });
  };

  const [addCarouselImpression, checkCarouselImpression] = useTrackImpressions();
  const [addGridImpression, checkGridImpression] = useTrackImpressions();

  return (
    <TrackingContext.Provider
      value={{
        track,
        trackBuilderInteraction,
        identify,
        addCarouselImpression,
        checkCarouselImpression,
        addGridImpression,
        checkGridImpression,
        isLoggedIn,
      }}
    >
      {children}
    </TrackingContext.Provider>
  );
};

export default TrackingContext;
